/** Wraps window.fetch. Idempotent — patches once, restorable. */
import { getRuntime } from '../capture.js';
import { newSpan, traceHeaders } from '../trace.js';
import { now, safeParseHeaders } from '../util.js';

let original: typeof fetch | null = null;

export function installFetch(): () => void {
  if (typeof window === 'undefined' || !window.fetch || original) return () => {};
  original = window.fetch.bind(window);
  const orig = original;

  window.fetch = async function arisFetch(
    input: RequestInfo | URL,
    init?: RequestInit,
  ): Promise<Response> {
    const rt = getRuntime();
    const url = typeof input === 'string' ? input : input instanceof URL ? input.href : input.url;
    const method = (init?.method ?? (input instanceof Request ? input.method : 'GET')) || 'GET';

    if (!rt || rt.shouldIgnore(url)) return orig(input, init);

    // Capture stack at the call site (before the async boundary loses it).
    const stack = new Error().stack;
    const span = newSpan();

    // Inline firewall: reject before the request leaves the browser.
    const policy = rt.enforce(url);
    if (policy.blocked) {
      const msg = `Blocked by Aris firewall (${policy.reason}): ${url}`;
      rt.capture({
        method, url, status: null, ok: false, durationMs: 0,
        error: { type: 'ArisFirewallBlock', message: msg },
        stack, span, blocked: true,
      });
      throw new TypeError(msg);
    }

    // Inject trace headers so the backend can continue this trace.
    const headers = new Headers(init?.headers ?? (input instanceof Request ? input.headers : undefined));
    const cause = 'unknown';
    for (const [k, v] of Object.entries(traceHeaders(span, cause))) headers.set(k, v);
    const patchedInit: RequestInit = { ...init, headers };

    const reqHeaders = safeParseHeaders(headers);
    const reqBodySize = estimateSize(init?.body ?? (input instanceof Request ? undefined : undefined));
    const start = now();

    try {
      const res = await orig(input, patchedInit);
      const respHeaders = safeParseHeaders(res.headers);
      rt.capture({
        method, url,
        status: res.status,
        ok: res.ok,
        durationMs: now() - start,
        requestHeaders: reqHeaders,
        responseHeaders: respHeaders,
        requestBodySize: reqBodySize,
        responseBodySize: numHeader(respHeaders['content-length']),
        error: null,
        stack,
        span,
      });
      return res;
    } catch (err) {
      rt.capture({
        method, url,
        status: null,
        ok: false,
        durationMs: now() - start,
        requestHeaders: reqHeaders,
        requestBodySize: reqBodySize,
        error: { type: (err as Error)?.name, message: (err as Error)?.message },
        stack,
        span,
      });
      throw err;
    }
  } as typeof fetch;

  return restoreFetch;
}

export function restoreFetch(): void {
  if (original) {
    window.fetch = original;
    original = null;
  }
}

function estimateSize(body: BodyInit | null | undefined): number | null {
  if (body == null) return null;
  if (typeof body === 'string') return body.length;
  if (body instanceof Blob) return body.size;
  if (body instanceof ArrayBuffer) return body.byteLength;
  return null;
}

function numHeader(v: string | undefined): number | null {
  if (!v) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}
